import { useEffect, useRef } from 'react';
import type { Message } from '@copilotkit/shared';
import { useAppDispatch } from '@/redux/store';
import { setMessages as setReduxMessages } from '@/redux/slices/copilotKitChatSlice';
import { sanitizeMessagesForRedux } from '@/helpers/aiChat/sanitizeMessages';

type UseCopilotKitSyncParams = {
  namespaceKey: string;
  messages: Message[];
  isLoading: boolean;
  isSyncingRef: React.MutableRefObject<boolean>;
  isCreatingNewConversation: boolean;
};

/**
 * Detects tool calls without corresponding tool results and adds synthetic results.
 * This handles cases where user navigates immediately after approving a tool,
 * before the API can return the actual tool result.
 */
const addMissingToolResults = (messages: Message[]): Message[] => {
  const toolCallIds = new Set<string>();
  const toolResultIds = new Set<string>();

  // Collect all tool call IDs and tool result IDs
  messages.forEach(msg => {
    if (msg.role === 'assistant' && 'toolCalls' in msg && msg.toolCalls) {
      msg.toolCalls.forEach(call => {
        if (call.id) toolCallIds.add(call.id);
      });
    }
    if (msg.role === 'tool' && 'toolCallId' in msg && msg.toolCallId) {
      toolResultIds.add(msg.toolCallId);
    }
  });

  // Find tool calls without results
  const missingToolResults = [...toolCallIds].filter(
    id => !toolResultIds.has(id)
  );

  // Return original array if no missing results
  if (missingToolResults.length === 0) {
    return messages;
  }

  // Add synthetic tool results for missing ones
  const messagesWithSynthetic = [...messages];
  missingToolResults.forEach(toolCallId => {
    messagesWithSynthetic.push({
      id: `synthetic-tool-result-${toolCallId}`,
      role: 'tool',
      toolCallId,
      content: 'Tool execution completed (navigation occurred)',
    } as Message);
  });

  return messagesWithSynthetic;
};

/**
 * Hook that syncs CopilotKit messages to Redux ONLY when agent is done generating.
 *
 * Currently this write has no active reader: CopilotKitAgentProvider is mounted at
 * the app level and never unmounts, so CopilotKit's own in-memory state is always
 * authoritative during a session.
 *
 * This becomes meaningful once Redux is backed by sessionStorage — at that point the
 * Redux messages written here will survive a page refresh and be restored by
 * useCopilotKitHydration.
 *
 * TODO: Wire up redux-persist (or equivalent) with sessionStorage to activate
 * the full persistence path.
 *
 * Key behaviors:
 * - Only persists complete messages (waits for isLoading to be false)
 * - Sanitizes messages to remove non-serializable properties
 * - Compares message signatures to prevent unnecessary Redux dispatches
 * - Sets isSyncingRef flag to prevent hydration loops
 * - On unmount, adds synthetic tool results for incomplete tool calls
 */
export const useCopilotKitSync = ({
  namespaceKey,
  messages,
  isLoading,
  isSyncingRef,
  isCreatingNewConversation,
}: UseCopilotKitSyncParams) => {
  const dispatch = useAppDispatch();
  const lastSyncedMessagesRef = useRef<string>('');

  // Store latest messages in ref for cleanup access
  const messagesRef = useRef<Message[]>(messages);
  // Synchronously mirror isCreatingNewConversation so the cleanup can read it
  // without depending on useEffect scheduling — a useEffect-based clear could
  // miss the unmount if React batches the state change with the key change.
  const isCreatingNewConversationRef = useRef(isCreatingNewConversation);
  isCreatingNewConversationRef.current = isCreatingNewConversation;

  // Update ref whenever messages change
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Main sync effect - runs when messages change
  useEffect(() => {
    // Skip if no messages
    if (messages.length === 0) {
      return;
    }

    // IMPORTANT: Only persist when agent is NOT generating
    // This prevents storing incomplete messages and reduces Redux dispatches
    if (isLoading) {
      return;
    }

    // Sanitize messages to remove non-serializable properties
    const sanitizedMessages = sanitizeMessagesForRedux(messages);

    // Create a stable string representation for comparison
    const messagesSignature = JSON.stringify(sanitizedMessages);

    // Only dispatch if messages have actually changed
    if (messagesSignature !== lastSyncedMessagesRef.current) {
      isSyncingRef.current = true;
      lastSyncedMessagesRef.current = messagesSignature;
      dispatch(
        setReduxMessages({
          namespaceKey,
          messages: sanitizedMessages,
        })
      );
      // Reset syncing flag immediately - dispatch is synchronous
      isSyncingRef.current = false;
    }
  }, [messages, namespaceKey, dispatch, isLoading, isSyncingRef]);

  // Cleanup effect - only runs on unmount
  // Ensures messages are persisted even when navigating immediately after tool approval
  useEffect(() => {
    return () => {
      if (isCreatingNewConversationRef.current) {
        return;
      }

      const currentMessages = messagesRef.current;

      // Skip if no messages to sync (we don't worry about in progress messages here)
      if (currentMessages.length === 0) {
        return;
      }

      // Add synthetic results for any incomplete tool calls
      const messagesToSync = addMissingToolResults(currentMessages);

      // Sanitize and check if sync is needed
      const sanitized = sanitizeMessagesForRedux(messagesToSync);
      const signature = JSON.stringify(sanitized);

      if (signature !== lastSyncedMessagesRef.current) {
        lastSyncedMessagesRef.current = signature;
        dispatch(
          setReduxMessages({
            namespaceKey,
            messages: sanitized,
          })
        );
      }
    };
    // Empty deps - cleanup only runs on unmount
  }, [namespaceKey, dispatch]);
};
